import { useEffect, useState } from 'react';

import {
  createUser,
  listUsers,
  ListUsersResponse,
  ManageableRole,
  updateUser,
  UserSummary,
} from '../api/users';
import { useAuth } from '../auth/useAuth';

export default function AdminUsersPage() {
  const { email: myEmail } = useAuth();
  const [data, setData] = useState<ListUsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busySub, setBusySub] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<ManageableRole>('developer');
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    listUsers()
      .then(setData)
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // False when user accounts live in the central identity-service: this page
  // then only assigns docs roles — no invites, no enable/disable.
  const lifecycleManaged = data?.lifecycleManaged ?? true;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateMessage(null);
    try {
      await createUser(newEmail.trim().toLowerCase(), newRole);
      setCreateMessage({
        text: lifecycleManaged ? `Invited ${newEmail}` : `Role granted to ${newEmail}`,
        ok: true,
      });
      setNewEmail('');
      load();
    } catch (err) {
      setCreateMessage({ text: String(err), ok: false });
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (user: UserSummary, role: ManageableRole) => {
    setBusySub(user.sub);
    try {
      await updateUser(user.sub, { role });
      load();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusySub(null);
    }
  };

  const handleToggleEnabled = async (user: UserSummary) => {
    setBusySub(user.sub);
    try {
      await updateUser(user.sub, { enabled: !user.enabled });
      load();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusySub(null);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 16px' }}>
      <h2>Users</h2>

      <form
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          margin: '16px 0 24px',
          padding: 16,
          border: '1px solid var(--border-subtle)',
          borderRadius: 8,
        }}
        onSubmit={handleCreate}
      >
        <strong style={{ fontSize: 14 }}>{lifecycleManaged ? 'Invite user' : 'Grant role'}</strong>
        <input
          placeholder="email@skymap.se"
          required={true}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid var(--border-strong)',
            fontSize: 13,
          }}
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
        />
        <select
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid var(--border-strong)',
            fontSize: 13,
          }}
          value={newRole}
          onChange={(e) => setNewRole(e.target.value as ManageableRole)}
        >
          <option value="developer">Developer</option>
          <option value="admin">Admin</option>
        </select>
        <button
          disabled={creating}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            background: 'var(--accent-solid)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 13,
          }}
          type="submit"
        >
          {creating ? 'Saving…' : lifecycleManaged ? 'Send invite' : 'Grant role'}
        </button>
        {createMessage && (
          <span
            style={{
              fontSize: 13,
              color: createMessage.ok ? 'var(--success-text)' : 'var(--danger-text)',
            }}
          >
            {createMessage.text}
          </span>
        )}
        {!lifecycleManaged && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', width: '100%' }}>
            Accounts and sign-in access are managed centrally (identity-service). This only assigns
            the docs role — the user must also hold a project grant to log in.
          </span>
        )}
      </form>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {error && <p style={{ color: 'var(--danger-text)' }}>{error}</p>}

      {data && !loading && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-subtle)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>Email</th>
              <th style={{ padding: '8px 4px' }}>Role</th>
              <th style={{ padding: '8px 4px' }}>Status</th>
              {lifecycleManaged && <th style={{ padding: '8px 4px' }}>Enabled</th>}
              <th style={{ padding: '8px 4px' }}>Created</th>
              {lifecycleManaged && <th style={{ padding: '8px 4px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr>
                <td
                  colSpan={lifecycleManaged ? 6 : 4}
                  style={{ padding: '16px 4px', color: 'var(--text-muted)' }}
                >
                  No users.
                </td>
              </tr>
            )}
            {data.items.map((user) => {
              const isSelf = user.email === myEmail;
              const roleManageable = user.role !== 'public';
              return (
                <tr key={user.sub} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '8px 4px' }}>
                    {user.email}
                    {isSelf && <span style={{ color: 'var(--text-muted)' }}> (you)</span>}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    {roleManageable ? (
                      <select
                        disabled={busySub === user.sub || isSelf}
                        style={{
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: '1px solid var(--border-strong)',
                          fontSize: 12,
                        }}
                        value={user.role}
                        onChange={(e) =>
                          void handleRoleChange(user, e.target.value as ManageableRole)
                        }
                      >
                        <option value="developer">Developer</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>none</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 4px', fontSize: 11, color: 'var(--text-muted)' }}>
                    {user.status}
                  </td>
                  {lifecycleManaged && (
                    <td style={{ padding: '8px 4px' }}>{user.enabled ? 'Yes' : 'No'}</td>
                  )}
                  <td style={{ padding: '8px 4px' }}>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                  </td>
                  {lifecycleManaged && (
                    <td style={{ padding: '8px 4px' }}>
                      <button
                        disabled={busySub === user.sub || isSelf}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 6,
                          border: '1px solid var(--border-strong)',
                          background: 'var(--bg-surface)',
                          color: user.enabled ? 'var(--danger-text)' : 'var(--success-text)',
                          cursor: isSelf ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          opacity: isSelf ? 0.5 : 1,
                        }}
                        onClick={() => void handleToggleEnabled(user)}
                      >
                        {user.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
